import { useEffect, useState } from 'react'

import { lib } from '@gocanvas/client'
const { validateWholeInteger, validateDecimalNumber } = lib

export const useSmartPreviewCurrencyValidation = ({
  defaultValue,
  minimum,
  maximum,
  value = defaultValue,
  showRequired,
}) => {
  const [isValid, setValidity] = useState(true)
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    if (value) {
      validateCurrencyField()
    } else if (showRequired) {
      setErrorText('Input is required')
      setValidity(false)
    } else {
      setValidity(true)
    }
  }, [value, showRequired])

  const validateCurrencyField = () => {
    const strippedInput = value.replace(/[^\d\.]/g, '')
    const isValid = validateDecimalNumber(strippedInput, 2)
    if (isValid) {
      const minMaxRangeValid = validateMinMaxRange(
        strippedInput,
        minimum,
        maximum,
        setErrorText
      )

      minMaxRangeValid ? setValidity(true) : setValidity(false)
    } else {
      setErrorText('Input is invalid')
      setValidity(false)
    }
  }

  return [isValid, errorText]
}

export const useSmartPreviewDecimalValidation = ({
  defaultValue,
  minimum,
  maximum,
  value = defaultValue,
  showRequired,
}) => {
  const [isValid, setValidity] = useState(true)
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    if (value) {
      validateDecimalField()
    } else if (showRequired) {
      setValidity(false)
      setErrorText('Input is required.')
    } else {
      setValidity(true)
    }
  }, [value, showRequired])

  const validateDecimalField = () => {
    const isValidDecimal = !isNaN(value)
    if (isValidDecimal) {
      const minMaxValid = validateMinMaxRange(
        parseFloat(value),
        parseInt(minimum),
        parseInt(maximum),
        setErrorText
      )
      minMaxValid ? setValidity(true) : setValidity(false)
    } else {
      setErrorText('Input is invalid')
      setValidity(false)
    }
  }
  return [isValid, errorText]
}

export const useDefaultValueDecimalValidation = ({
  minimum,
  maximum,
  value = defaultValue,
  mask,
}) => {
  const [isValid, setValidity] = useState(true)
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    if (value) {
      validateDecimalField()
    } else {
      setValidity(true)
    }
  }, [value, minimum, maximum])

  const validateDecimalField = () => {
    const isValidDecimal = validateDecimalNumber(value, mask)
    if (isValidDecimal) {
      const minMaxValid = validateMinMaxRange(
        parseFloat(value),
        parseInt(minimum),
        parseInt(maximum),
        setErrorText
      )
      minMaxValid ? setValidity(true) : setValidity(false)
    } else {
      setErrorText('Input is invalid')
      setValidity(false)
    }
  }
  return [isValid, errorText]
}

export const useSmartPreviewNumberValidation = ({
  defaultValue,
  minimum,
  maximum,
  value = defaultValue,
  showRequired,
}) => {
  const [isValid, setValidity] = useState(true)
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    if (value) {
      validateNumberField()
    } else if (showRequired) {
      setValidity(false)
      setErrorText('Input is required.')
    } else {
      setValidity(true)
    }
  }, [value, showRequired])

  const validateNumberField = () => {
    const isValidInteger = validateWholeInteger(value)
    if (isValidInteger) {
      const isMinMaxValid = validateMinMaxRange(
        parseInt(value),
        parseInt(minimum),
        parseInt(maximum),
        setErrorText
      )
      isMinMaxValid ? setValidity(true) : setValidity(false)
    } else {
      setErrorText('Input is invalid')
      setValidity(false)
    }
  }
  return [isValid, errorText]
}

export const useDefaultValueNumberValidation = ({
  minimum,
  maximum,
  value,
}) => {
  const [isValid, setValidity] = useState(true)
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    if (value) {
      validateNumberField()
    } else {
      setValidity(true)
    }
  }, [value, minimum, maximum])

  const validateNumberField = () => {
    const isValidInteger = validateWholeInteger(value)
    if (isValidInteger) {
      const isMinMaxValid = validateMinMaxRange(
        parseInt(value),
        parseInt(minimum),
        parseInt(maximum),
        setErrorText
      )
      isMinMaxValid ? setValidity(true) : setValidity(false)
    } else {
      setErrorText('Input is invalid')
      setValidity(false)
    }
  }
  return [isValid, errorText]
}

export const useSmartPreviewTextValidation = ({ showRequired, value }) => {
  const [isValid, setValidity] = useState(true)
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    if (!value && showRequired) {
      setErrorText('This field is required')
      setValidity(false)
    } else {
      setValidity(true)
    }
  }, [value, showRequired])

  return [isValid, errorText]
}

const validateMinMaxRange = (
  value,
  minimum,
  maximum,
  setErrorText = () => {}
) => {
  if (minimum && !maximum) {
    if (value >= minimum) {
      return true
    } else {
      setErrorText(`Input must be greater than or equal to ${minimum}`)
      return false
    }
  }
  //has max but no min
  else if (maximum && !minimum) {
    if (value <= maximum) {
      return true
    } else {
      setErrorText(`Input must be less than or equal to ${maximum}`)
      return false
    }
  }
  //has both min and max and input value is within the range
  else if (minimum && maximum) {
    if (value >= minimum && value <= maximum) {
      setErrorText('')
      return true
    } else if (value > maximum) {
      setErrorText(`Input must be less than or equal to ${maximum}`)
      return false
    } else {
      setErrorText(`Input must be greater than or equal to ${minimum}`)
      return false
    }
  }
  setErrorText('')
  return true
}
